import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, combineLatest, filter, map, Observable, of, Subscription, switchMap } from 'rxjs';
import { DatePickerMonthChangeEvent } from 'primeng/datepicker';
import { DateTime } from 'luxon';

import { WorkedShiftsService } from '../../services/worked-shifts.service';
import { ShiftStatus, WorkedShift } from '../../models/worked-shift';
import { LockStatus } from '../../models/lock-status';
import { LockState } from '../../models/lock-state';

export interface DateInfo {
    date: DateTime;

    workedShifts: WorkedShift[];
}

@Component({
    selector: 'lib-worked-calendar',
    templateUrl: './worked-calendar.component.html',
    styleUrls: ['./worked-calendar.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => WorkedCalendarComponent),
        }
    ],
    standalone: false
})
export class WorkedCalendarComponent implements OnInit, OnDestroy, ControlValueAccessor {

    @Input() set siteId(id: string) {
        this.siteId$.next(id);
    }

    private _allowEmptyDates = new BehaviorSubject<boolean>(false);

    _numberOfMonths = new BehaviorSubject<number>(1);

    @Input() set allowEmptyDates(allow: boolean) {
        this._allowEmptyDates.next(allow);
    }

    @Input() showOtherMonths: boolean = true;
    @Input() tabindex: number | null = null;
    @Input() inputId: string | null = null;
    @Input() name: string | null = null;
    @Input() disabled: boolean = false;
    @Input() readonly: boolean = false;
    @Input() inline: boolean = false;
    @Input() selectionMode: ('single' | 'multiple' | 'range') = 'single';
    @Input() set numberOfMonths(val: number) {
        this._numberOfMonths.next(val);
    }

    @Output() onFocus = new EventEmitter<void>();
    @Output() onMonthChange = new EventEmitter<{ month: number, year: number }>();
    @Output() onSelect = new EventEmitter<DateInfo | DateInfo[]>();

    _selection: Date | Date[] | null = null;
    get selection(): Date | Date[] | null {
        return this._selection;
    }

    set selection(val: Date | Date[] | null) {
        this._selection = val;
        setTimeout(() => this.ValueChanged(), 0);
    }

    siteId$ = new BehaviorSubject<string>('');
    displayed = new BehaviorSubject<{ start: DateTime, end: DateTime }>({
        start: DateTime.local().startOf('month'),
        end: DateTime.local().endOf('month').startOf('day')
    });
    workedShifts = new BehaviorSubject<WorkedShift[]>([]);
    disabledDates: Observable<Date[]>;

    subscriptions: Subscription[];

    constructor(private workedShiftsService: WorkedShiftsService) {
        this.disabledDates = combineLatest([
            this.displayed,
            this.workedShifts,
            this._allowEmptyDates
        ]).pipe(map(([displayed, shifts, allowEmptyDates]) => {
            if (allowEmptyDates)
                return [];

            function* genDays(start: DateTime, end: DateTime): IterableIterator<DateTime> {
                for(let day = start; day <= end; day = day.plus({days: 1}))
                    yield day;
            }

            const shiftDates = shifts.map(s => s.date);

            return [...genDays(displayed.start, displayed.end)]
                .filter(d => !shiftDates.some(sd => sd.equals(d)))
                .map(d => d.toJSDate());
        }));
    }

    ngOnInit(): void {
        this.subscriptions = [
            combineLatest([
                this.siteId$.pipe(filter(s => !!s)),
                this.displayed
            ]).pipe(switchMap(([siteId, displayed]) => {
                return this.workedShiftsService.FollowByDateRange(siteId, displayed.start, displayed.end);
            })).subscribe(shifts => {
                this.workedShifts.next(shifts);
            })
        ]
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    writeValue(obj: Date | Date[] | null): void {
        const dt = DateTime.fromJSDate(Array.isArray(obj) ? obj[0] : obj);

        if (dt.isValid)
            this.ChangeMonth({ year: dt.year, month: dt.month });

        this.selection = obj;
    }

    OnChange: (value: Date | Date[] | null) => void = _ => {};

    registerOnChange(fn: any): void {
        this.OnChange = fn;
    }

    OnTouched: (_: void) => any = () => {};

    registerOnTouched(fn: any): void {
        this.OnTouched = fn;
    }
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    OnFocus(): void {
        this.onFocus.emit();
        this.OnTouched();
    }

    ChangeMonth(event: DatePickerMonthChangeEvent): void {
        // luxon .weekday 1 = Monday, 7 = sunday
        let start = DateTime.fromObject({ year: event.year, month: event.month, day: 1 }); // 1st of month
        start = start.minus({ days: start.weekday % 7 }) // to leading sunday, modulus prevents 1st on sunday going to previous week.

        let end = DateTime.fromObject({ year: event.year, month: event.month, day: 1 }).plus({ months: this._numberOfMonths.value }).minus({ days: 1 }); // last of month
        end = end.plus({ days: 6 - (end.weekday % 7) }); // to following saturday, modulus prevents 1st on sunday going to previous week.

        this.displayed.next({ start, end });
        this.onMonthChange.emit({ month: event.month, year: event.year });
    }

    ValueChanged(): void {
        this.OnTouched();

        this.OnChange(this.selection);
    }

    OnSelect(date: Date | Date[]): void {
        this.ValueChanged();

        const toDateInfo = (date: Date) => {
            const dt = DateTime.fromJSDate(date);
            return {
                date: dt,
                workedShifts: this.workedShifts.value.filter(ws => ws.date.equals(dt))
            }
        }

        this.onSelect.emit(Array.isArray(this.selection)
            ? this.selection.map(v => toDateInfo(v))
            : toDateInfo(this.selection));
    }

    GetShiftStatuses(dateObj: { year: number, month: number, day: number }): Observable<('Open' | 'Closed' | 'Locked')[]> {
        return combineLatest([
            of(DateTime.fromObject({ year: dateObj.year, month: dateObj.month + 1, day: dateObj.day })),
            this.workedShifts
        ]).pipe(map(([date, workedShifts]) => {
            const dayShifts = workedShifts
                .filter(ws => date.equals(ws.date));

            return dayShifts
                .sort((l, r) => l.shift.number - r.shift.number)
                .map(ws => {
                    switch (ws.shiftStatus) {
                        case ShiftStatus.Open:
                            return 'Open';
                        case ShiftStatus.Closed:
                            return 'Closed';
                        case ShiftStatus.Locked:
                            return 'Locked';
                    }
                });
        }));
    }
}
